import DateStringInput from '@repo/ui/date-string-input'
import Input from '@repo/ui/input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import Switch from '@repo/ui/switch'
import { useQuery } from '@tanstack/react-query'

import { getCenters } from '../../../api/centers/get-centers'
import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getPeriods } from '../../../api/periods/get-periods'
import { getRads } from '../../../api/rads/get-rads'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  MissionStatusEnum,
  missionStatusEnumMapping
} from '../../../lib/types/enums/mission/mission-status.enum'
import {
  AccomodationCategoryEnum,
  accomodationCategoryEnumMapping
} from '../../../lib/types/enums/product/accomodation-category.enum'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../../lib/types/enums/staff/staff-position.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { StaffRequestFilters } from '../../../lib/types/models/staff-request/staff-request-input'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'

interface Props {
  filter?: StaffRequestFilters
  onFilterChange: (filter: StaffRequestFilters) => void
}

export function StaffRequestsFilters({ filter, onFilterChange }: Props) {
  const { t } = useTranslate()

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: centers } = useQuery({
    queryKey: ['centers'],
    queryFn: () => getCenters()
  })

  const { data: rads } = useQuery({
    queryKey: ['rads'],
    queryFn: () => getRads()
  })

  function setFilter<K extends keyof StaffRequestFilters>(key: K, value: StaffRequestFilters[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  return (
    <div className={'flex flex-col gap-3'}>
      <div />

      <Switch
        label="Masquer les séjours terminés"
        labelVariant="label-medium"
        checked={!!filter?.hideFinishedStays}
        onCheckedChange={(checked) => setFilter('hideFinishedStays', checked || undefined)}
      />

      <MultiSelectInput
        searchable
        variant="primary"
        label="Qualifications"
        options={Object.values(StaffQualificationEnum).map((value) => ({
          label: t(staffQualificationEnumMapping[value].label),
          value
        }))}
        onChange={(options) => setFilter('qualifications', options)}
        selectedOptions={filter?.qualifications?.map((qualification) => ({
          label: t(staffQualificationEnumMapping[qualification].label),
          value: qualification
        }))}
      />

      <MultiSelectInput
        searchable
        variant="primary"
        label="Formules"
        options={
          formulas?.data.map((formula) => ({
            label: getTranslatedName(formula),
            value: formula.id
          })) ?? []
        }
        onChange={(options) => setFilter('formulas', options)}
        selectedOptions={filter?.formulas?.map((formula) => ({
          label: getTranslatedName(formulas?.data.find((f) => f.id === formula)),
          value: formula
        }))}
      />

      <MultiSelectInput
        searchable
        variant="warning"
        label="Périodes"
        options={
          periods?.data.map((period) => ({
            label: period.name,
            value: period.name
          })) ?? []
        }
        onChange={(options) => setFilter('periods', options)}
        selectedOptions={filter?.periods?.map((period) => ({
          label: period,
          value: period
        }))}
      />

      <MultiSelectInput
        searchable
        variant="primary"
        label="Pays"
        options={
          countries?.data.map((country) => ({
            label: country.name,
            value: country.name
          })) ?? []
        }
        onChange={(options) => setFilter('countries', options)}
        selectedOptions={filter?.countries?.map((country) => ({
          label: country,
          value: country
        }))}
      />

      <MultiSelectInput<MissionStatusEnum>
        variant="primary"
        label="Statut"
        options={Object.values(MissionStatusEnum).map((type) => ({
          label: missionStatusEnumMapping[type].label,
          value: type
        }))}
        onChange={(options) => setFilter('status', options)}
        selectedOptions={filter?.status?.map((status) => ({
          label: missionStatusEnumMapping[status].label,
          value: status
        }))}
      />

      <MultiSelectInput<StaffPositionEnum>
        variant="primary"
        label="Poste"
        options={Object.values(StaffPositionEnum)
          .filter((position) => position !== StaffPositionEnum.AAT)
          .map((position) => ({
            label: t(staffPositionEnumMapping[position]),
            value: position
          }))}
        onChange={(options) => setFilter('positions', options)}
        selectedOptions={filter?.positions?.map((position) => ({
          label: t(staffPositionEnumMapping[position]),
          value: position
        }))}
      />

      <div className="flex space-x-4 justify-between">
        <DateStringInput
          label="Date de début"
          mode="over"
          value={filter?.startDate}
          onChange={(date) => setFilter('startDate', date)}
        />
        <DateStringInput
          label="Date de fin"
          mode="over"
          value={filter?.endDate}
          onChange={(date) => setFilter('endDate', date)}
        />
      </div>

      <Input
        type="number"
        label="Durée (jours)"
        value={filter?.duration}
        onChange={(e) => setFilter('duration', Number(e.target.value))}
      />

      <MultiSelectInput
        searchable
        variant="primary"
        label="Centre"
        options={
          centers?.data.map((center) => ({
            label: center.name,
            value: center.id
          })) ?? []
        }
        onChange={(options) => setFilter('centerIds', options)}
        selectedOptions={filter?.centerIds?.map((center) => ({
          label: centers?.data.find((c) => c.id === center)?.name ?? '',
          value: center
        }))}
      />

      <SelectInput<AccomodationCategoryEnum>
        label="Type d'hébergement"
        options={Object.values(AccomodationCategoryEnum).map((category) => ({
          label: accomodationCategoryEnumMapping[category],
          value: category
        }))}
        onChange={(category) => setFilter('accomodationCategory', category)}
        selectedOption={
          filter?.accomodationCategory
            ? {
                label: accomodationCategoryEnumMapping[filter.accomodationCategory],
                value: filter.accomodationCategory
              }
            : undefined
        }
      />

      <SelectInput
        label="RAD"
        options={
          rads?.data.map((rad) => ({
            label: rad.title,
            value: rad.id
          })) ?? []
        }
        onChange={(options) => setFilter('radId', options)}
        selectedOption={
          filter?.radId
            ? {
                label: rads?.data.find((c) => c.id === filter.radId)?.title ?? '',
                value: filter.radId
              }
            : undefined
        }
      />
    </div>
  )
}
